import type { Car, Driver } from '@multirx/shared';
import { carPower } from '@multirx/shared/types/car.js';
import { driverPower } from '@multirx/shared/types/driver.js';
import {
  formatDriverTitleChips,
  type DriverSeasonProfile,
} from '@multirx/shared/game/driverPalmares.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import { formatDriverDisplayName } from '@multirx/shared/game/driverDisplayLabel.js';
import { getOwnDriverId } from '../auth/ownDriverLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { renderStarsCar, renderStarsDriver } from '../components/starRating.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { resolveCar3dModelUrl } from '../cars/car3dModelMap.js';

export type FeaturedPilotCarModel = {
  name: string;
  engine: number;
  tire: number;
  power: number;
  imageUrl: string | null;
  modelUrl: string | null;
};

export type FeaturedPilotPalmaresModel = {
  titles: string[];
  championshipPoints: number | null;
  rank: number | null;
  finalsWon: number;
  podiums: number;
};

export type FeaturedPilotCardModel = {
  driverId: number;
  fullName: string;
  nationality: string;
  division: number | undefined;
  divisionLabel: string;
  pilotage: number;
  defense: number;
  driverPower: number;
  driverImageUrl: string | null;
  driverImagePath: string | null;
  car: FeaturedPilotCarModel | null;
  palmares: FeaturedPilotPalmaresModel;
};

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function formatFeaturedPilotName(model: FeaturedPilotCardModel): string {
  return formatDriverDisplayName(model.fullName, model.driverId, getOwnDriverId());
}

function divisionShortLabel(division: number | undefined): string {
  if (division == null) return '—';
  return DIVISIONS.find((d) => d.id === division)?.shortLabel ?? `D${division}`;
}

export function buildFeaturedPilotCardModel(
  driver: Driver,
  car: Car | null,
  profile: DriverSeasonProfile | null
): FeaturedPilotCardModel {
  const driverId = driver.id ?? 0;
  const imagePath = driver.imagePath?.trim() || null;
  const carImage = car?.imagePath?.trim() || null;
  const titles = profile?.titles?.length ? formatDriverTitleChips(profile.titles) : [];
  const season = profile?.season ?? null;

  return {
    driverId,
    fullName: `${driver.firstName} ${driver.lastName}`.trim(),
    nationality: driver.nationality,
    division: driver.division,
    divisionLabel: divisionShortLabel(driver.division),
    pilotage: driver.pilotage ?? 0,
    defense: driver.defense ?? 0,
    driverPower: driverPower(driver),
    driverImageUrl: imagePath ? resolveMediaUrl(imagePath) : null,
    driverImagePath: imagePath,
    car: car
      ? {
          name: car.name?.trim() || 'Voiture',
          engine: car.engine ?? 0,
          tire: car.tire ?? 0,
          power: carPower(car),
          imageUrl: carImage ? resolveMediaUrl(carImage) : null,
          modelUrl: resolveCar3dModelUrl(car),
        }
      : null,
    palmares: {
      titles,
      championshipPoints: season?.championshipPoints ?? null,
      rank: season?.rank ?? null,
      finalsWon: season?.finalsWon ?? 0,
      podiums: season?.podiums ?? 0,
    },
  };
}

export function featuredPilotCardInnerHtml(model: FeaturedPilotCardModel): string {
  const flag = nationalityFlagMarkup(model.nationality);

  const carBlock = model.car
    ? `
      <div class="featured-pilot-car-meta">
        <strong class="featured-pilot-car-name">${escapeHtml(model.car.name)}</strong>
        <div class="featured-pilot-stats">
          <div class="featured-pilot-stat">
            <span class="featured-pilot-stat-label">Moteur</span>
            ${renderStarsCar(model.car.engine)}
          </div>
          <div class="featured-pilot-stat">
            <span class="featured-pilot-stat-label">Pneus</span>
            ${renderStarsCar(model.car.tire)}
          </div>
          <div class="featured-pilot-stat featured-pilot-stat--total">
            <span class="featured-pilot-stat-label">Total</span>
            <strong>${model.car.power}★</strong>
          </div>
        </div>
      </div>`
    : `<p class="featured-pilot-muted">Pas de voiture attribuée</p>`;

  const avatar = model.driverImageUrl
    ? `<img src="${escapeHtml(model.driverImageUrl)}" alt="" class="featured-pilot-avatar-img" loading="lazy" />`
    : `<span class="featured-pilot-avatar-fallback" aria-hidden="true">${escapeHtml(model.fullName.slice(0, 1) || '?')}</span>`;

  const carPhoto = model.car?.imageUrl
    ? `<img src="${escapeHtml(model.car.imageUrl)}" alt="${escapeHtml(model.car.name)}" class="featured-pilot-car-photo-img" loading="lazy" />`
    : '';

  return `
    <div class="featured-pilot-layout" data-featured-pilot-id="${model.driverId}">
      <div class="featured-pilot-car-3d" data-featured-pilot-car-3d aria-hidden="true"></div>
      <div class="featured-pilot-media" aria-hidden="true">
        <div class="featured-pilot-avatar" data-featured-pilot-avatar>${avatar}</div>
        <div class="featured-pilot-car-photo${carPhoto ? ' is-ready' : ''}" data-featured-pilot-car-photo>${carPhoto}</div>
      </div>
      <div class="featured-pilot-body">
        <p class="featured-pilot-eyebrow">Pilote vedette · ${escapeHtml(model.divisionLabel)}</p>
        <p class="heading featured-pilot-name">${escapeHtml(formatFeaturedPilotName(model))} ${flag}</p>
        <div class="featured-pilot-stats">
          <div class="featured-pilot-stat">
            <span class="featured-pilot-stat-label">Pilotage</span>
            ${renderStarsDriver(model.pilotage)}
          </div>
          <div class="featured-pilot-stat">
            <span class="featured-pilot-stat-label">Défense</span>
            ${renderStarsDriver(model.defense)}
          </div>
          <div class="featured-pilot-stat featured-pilot-stat--total">
            <span class="featured-pilot-stat-label">Total</span>
            <strong>${model.driverPower}★</strong>
          </div>
        </div>
        ${carBlock}
        <p class="featured-pilot-cta">Voir la fiche →</p>
      </div>
    </div>`;
}
